'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import View from '@/components/view';
import configuration from './config';
import { motion } from 'framer-motion';
import MarkerViewCard from './marker-view';
import { Spinner } from '@nextui-org/react';
import { LocaleProps } from '@/typings/next-page';

const Globe = dynamic(() => import('react-globe.gl'), {
  ssr: false,
  loading: () => (
    <View className="h-screen grid place-content-center">
      <Spinner color="current" />
    </View>
  ),
});

type Place = {
  id: string | undefined;
  city: any;
  latitude: any;
  longitude: any;
  altitude: number;
  color: string;
};

type Props = {
  places: Place[] | undefined;
} & LocaleProps;

const WorldGlobe = ({ places, params }: Props) => {
  const [currentMarker, setCurrentMarker] = React.useState<Place | null>(null);
  const globeRef = React.useRef<any>(null);

  const globeReady = () => {
    if (globeRef.current) {
      globeRef.current.controls().autoRotate = true;
      globeRef.current.controls().enableZoom = false;
      globeRef.current.pointOfView({
        lat: places?.[0]?.latitude,
        lng: places?.[0]?.longitude,
        altitude: 1.8,
      });
    }
  };
  return (
    <View className="overflow-hidden relative">
      <Globe
        {...configuration}
        ref={globeRef}
        height={600}
        labelsData={places}
        labelText={(d: any) => d?.city}
        labelColor={(d: any) => d?.color}
        labelLat={(d: any) => d?.latitude}
        labelLng={(d: any) => d?.longitude}
        onLabelClick={(value: any) => setCurrentMarker(value)}
        onGlobeReady={globeReady}
        backgroundColor="#08070e"
        rendererConfig={{ antialias: true, alpha: true }}
        atmosphereColor="#5784a7"
        atmosphereAltitude={0.5}
      />
      {currentMarker?.id && (
        <MarkerViewCard
          params={params}
          projectId={currentMarker?.id}
          onClose={() => setCurrentMarker(null)}
        />
      )}
      <motion.div
        className="flex items-center justify-center absolute bottom-2 sm:bottom-8 cursor-pointer w-full"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        onClick={() =>
          typeof window !== 'undefined' &&
          window?.scrollBy({ top: window?.innerHeight, behavior: 'smooth' })
        }
      >
        <motion.img
          width={48}
          height={48}
          alt="Scroll Trigger"
          src="/icons/scroll.gif"
          className="h-8 w-8 sm:h-12 sm:w-12"
        />
      </motion.div>
    </View>
  );
};

export default WorldGlobe;
